import analyticsApi from "./analyticsApi";
import {
  announceMessageFromData,
  APARTMENT_KEY,
  AREA_KEY,
  COSTUNIT_KEY,
  getElementFromLocalStorage,
  getKey,
  getLocalStorage,
  getParsedObject,
} from "./hoasFavouritesHelpers";

const FAVOURITE_BUTTON_CLASSES = [
  "favourite",
  "favourite-heart",
  "single-heart",
];

/**
 * Updates header favourites counters and
 * nitialize all favouritebuttons and add event-listener for click
 * @returns {void}
 */
export const initializeAllFavouriteButtons = () => {
  handleFavouriteImports();

  // First update header counter
  mobileHeaderFavouritesCounter();

  // Then initialize the initial state of localstorage buttons
  const buttons = document.querySelectorAll(
    getClassesJoined(FAVOURITE_BUTTON_CLASSES),
  );

  if (!buttons) {
    return;
  }

  // Iterate and initialize all favourite buttons
  buttons.forEach(function (favouriteButton) {
    if (!favouriteButton) {
      return;
    }

    let favouriteData = getParsedObject(
      favouriteButton.getAttribute("data-favourite-object"),
    );

    if (!favouriteData) {
      return;
    }

    listenFavouriteButton(favouriteButton);

    const text = favouriteButton.querySelector(".modified-text");
    const isFound = getElementFromLocalStorage(favouriteData);

    if (!isFound) {
      return;
    }

    favouriteButton.classList.add("active");

    if (text) {
      text.textContent = favouriteButton.getAttribute(
        "data-remove-button-text",
      );
    }
  });
};

/**
 * Counts favourites for the header and refreshes the value in the dom
 * @return {void}
 */
export const mobileHeaderFavouritesCounter = () => {
  const areas = getLocalStorage(AREA_KEY);
  const costUnits = getLocalStorage(COSTUNIT_KEY);
  const apartments = getLocalStorage(APARTMENT_KEY);

  let count = 0;

  if (Array.isArray(areas) && areas.length > 0) {
    count += areas.length;
  }

  if (Array.isArray(costUnits) && costUnits.length > 0) {
    count += costUnits.length;
  }

  if (Array.isArray(apartments) && apartments.length > 0) {
    count += apartments.length;
  }

  const counterElements = document.querySelectorAll(".headerFavouriteCounter");

  if (counterElements?.length > 0 && count > 0) {
    counterElements.forEach((elem) => {
      elem.classList.remove("d-none");
      elem.textContent = count;
    });
    return;
  }
  counterElements.forEach((elem) => {
    elem.classList.add("d-none");
  });
};

/**
 * Add eventlistener for favourite-button
 * @returns {void}
 */
const listenFavouriteButton = (favouriteButton) => {
  favouriteButton.addEventListener("click", function (e) {
    const noneFound = FAVOURITE_BUTTON_CLASSES.every(
      (item) => !favouriteButton?.classList?.contains(item),
    );

    if (noneFound) {
      return e;
    }
    favouriteButton.classList.add("active");
    handleFavouritesButtonClick(favouriteButton);
  });
};

/**
 * On favourites button click add or remove from localstorage
 * @param {*} favouriteButton
 * @returns {void}
 */
const handleFavouritesButtonClick = (favouriteButton) => {
  if (!favouriteButton) {
    return;
  }

  const favouriteObject = getParsedObject(
    favouriteButton.getAttribute("data-favourite-object"),
  );

  if (!favouriteObject) {
    return;
  }

  const foundElement = getElementFromLocalStorage(favouriteObject);
  const text = favouriteButton.querySelector(".modified-text");

  if (foundElement) {
    // Element is currently in localStorage, so remove it
    window.hoasFavourites.removeDataByKey(
      favouriteObject?.type,
      favouriteObject,
    );

    favouriteButton.classList.remove("active");

    if (text) {
      text.textContent = favouriteButton.getAttribute("data-add-button-text");
    }
    setSimilarFavouriteButtonsState(favouriteObject, false);
    //Screen readers only, Announce message when removing from favourites
    const message = favouriteButton.getAttribute(
      "data-remove-announcer-message",
    );
    announceMessageFromData(
      favouriteObject,
      getKey(favouriteObject?.type),
      message,
    );
    return;
  }

  const { generateAnalyticsAddToCart } = analyticsApi();

  //Generate analytics Datalayer when added to localStorage/favourites
  generateAnalyticsAddToCart(favouriteObject);
  //Not found in localstorage so add it
  window.hoasFavourites.addDataByKey(favouriteObject?.type, favouriteObject);
  favouriteButton.classList.add("active");
  setSimilarFavouriteButtonsState(favouriteObject, true);
  const message = favouriteButton.getAttribute("data-add-announcer-message");
  announceMessageFromData(
    favouriteObject,
    getKey(favouriteObject?.type),
    message,
  );
};

/**
 * Import favourites - apartmentObjects - from window.importedFavourites, and add it to favourites.
 * Remove the url parameter and show success message.
 * @returns {void}
 */
const handleFavouriteImports = () => {
  if (!window.importedFavourites || !window.importedFavourites.apartmentToAdd) {
    return;
  }

  window.hoasFavourites.addDataByKey(
    "apartments",
    window.importedFavourites.apartmentToAdd,
  );

  // Remove the 'fav_ap' parameter from the URL
  const url = new URL(window.location.href);
  url.searchParams.delete("fav_ap");
  window.history.replaceState({}, "", url);

  // Generate success dialog
  const breadcrumb = document.querySelector(".element-breadcrumb");
  if (!breadcrumb) {
    return;
  }

  const dialog = document.createElement("div");
  dialog.className = "dialog success";
  dialog.textContent =
    window.importedFavourites.message || "Asunto lisätty suosikkeihin.";
  breadcrumb.parentNode.insertBefore(dialog, breadcrumb.nextSibling);
};

/**
 *
 * @param {*} currentFavouriteObject parsed object from currently clicked favourite-button
 * @param {*} useRemoveText To determine if the element needs to have remove-text and not active class
 * @returns
 */
const setSimilarFavouriteButtonsState = (
  currentFavouriteObject,
  useRemoveText = false,
) => {
  //This way we can be sure that every button with same data will respond to action in the page
  //We iterate each button and modify them if they have same data as the target button
  //This consumes a bit more memory but is much simple to read, and will be sufficient enough since each page will not have millions of cards
  if (
    !currentFavouriteObject ||
    (!currentFavouriteObject?.address && !currentFavouriteObject?.name)
  ) {
    //There is no current target element or it's attributes are empty
    return;
  }

  const allFavourites = document.querySelectorAll(
    getClassesJoined(FAVOURITE_BUTTON_CLASSES),
  );

  if (!allFavourites?.length) {
    return;
  }

  allFavourites.forEach((button) => {
    const favouriteData = getParsedObject(
      button.getAttribute("data-favourite-object"),
    );

    if (!favouriteData) {
      return;
    }

    if (!favouriteData?.address && !favouriteData?.name) {
      // Early out --> Not area, not cost-unit nor apartment
      return;
    }

    if (
      currentFavouriteObject.address !== favouriteData.address ||
      currentFavouriteObject.name !== favouriteData.name ||
      currentFavouriteObject?.id !== favouriteData?.id
    ) {
      //Target object is not the same as currently iterating element
      return;
    }

    const text = button.querySelector(".modified-text");

    if (useRemoveText) {
      //The element is being added so use removeText!
      button.classList.add("active");
      if (text) {
        text.textContent = button.getAttribute("data-remove-button-text");
      }
      return;
    }

    //The element is being removed, use add text
    button.classList.remove("active");
    if (text) {
      text.textContent = button.getAttribute("data-add-button-text");
    }
  });
};
/**
 * Helper function to return array of items as string.
 * Adds a dot before each item and separates each item with a comma and a space
 * @param {any} array
 * @returns {string}
 */
const getClassesJoined = (array) => {
  return array.map((item) => "." + item).join(", ");
};
